import { useId, useState } from "react";

import type { MotionPreference, Settings } from "../../platform/settings/settingsStore";

import "./SettingsPanel.css";

export type SettingsCapabilities = Readonly<{
  sound: boolean;
  haptics: boolean;
}>;

export type SettingsPanelProps = Readonly<{
  settings: Settings;
  capabilities: SettingsCapabilities;
  onChange: (settings: Settings) => Promise<boolean> | boolean;
  onClose: () => void;
}>;

const MOTION_OPTIONS: readonly Readonly<{
  value: MotionPreference;
  label: string;
  description: string;
}>[] = [
  { value: "system", label: "Follow device", description: "Use your device motion preference." },
  { value: "full", label: "Full", description: "Show the complete tabletop choreography." },
  { value: "reduced", label: "Reduced", description: "Use short, direct transitions." },
];

/** A controlled settings surface. Platform capability and persistence stay at the app edge. */
export function SettingsPanel({ settings, capabilities, onChange, onClose }: SettingsPanelProps) {
  const titleId = useId();
  const motionLegendId = useId();
  const optionId = useId();
  const [saveFailed, setSaveFailed] = useState(false);

  const update = async (next: Settings): Promise<void> => {
    setSaveFailed(!(await onChange(Object.freeze(next))));
  };

  return (
    <section className="settings-panel" aria-labelledby={titleId}>
      <header className="settings-panel__header">
        <div>
          <h2 id={titleId}>Settings</h2>
        </div>
        <button className="settings-panel__close" type="button" onClick={onClose}>
          Done
        </button>
      </header>

      <div className="settings-panel__toggles">
        <SettingToggle
          available={capabilities.sound}
          checked={settings.soundEnabled}
          description="Quiet ceramic tones for contact, movement, and results."
          label="Sound"
          onChange={(checked) => void update({ ...settings, soundEnabled: checked })}
        />
        <SettingToggle
          available={capabilities.haptics}
          checked={settings.hapticsEnabled}
          description="Short vibration cues on devices that support them."
          label="Haptics"
          onChange={(checked) => void update({ ...settings, hapticsEnabled: checked })}
        />
        <SettingToggle
          available
          checked={settings.discMarkersEnabled}
          description="Circle for black, X for red."
          label="Disc markings"
          onChange={(checked) => void update({ ...settings, discMarkersEnabled: checked })}
        />
      </div>

      <fieldset className="settings-panel__motion" aria-labelledby={motionLegendId}>
        <legend id={motionLegendId}>Motion</legend>
        {MOTION_OPTIONS.map((option) => {
          const id = `${optionId}-${option.value}`;
          return (
            <label className="settings-panel__motion-option" htmlFor={id} key={option.value}>
              <input
                checked={settings.motion === option.value}
                id={id}
                name={`${optionId}-motion`}
                onChange={() => void update({ ...settings, motion: option.value })}
                type="radio"
                value={option.value}
              />
              <span>
                <strong>{option.label}</strong>
                <small>{option.description}</small>
              </span>
            </label>
          );
        })}
      </fieldset>

      {saveFailed ? (
        <p className="settings-panel__warning" role="status">
          This preference could not be saved. It still applies until you close the app.
        </p>
      ) : null}
    </section>
  );
}

type SettingToggleProps = Readonly<{
  available: boolean;
  checked: boolean;
  description: string;
  label: string;
  onChange: (checked: boolean) => void;
}>;

function SettingToggle({ available, checked, description, label, onChange }: SettingToggleProps) {
  const id = useId();
  return (
    <label className="settings-panel__toggle" htmlFor={id}>
      <span>
        <strong>{label}</strong>
        <small>{available ? description : "Not available on this device."}</small>
      </span>
      <input
        checked={available && checked}
        disabled={!available}
        id={id}
        onChange={(event) => onChange(event.currentTarget.checked)}
        type="checkbox"
      />
      <span aria-hidden="true" className="settings-panel__switch" />
    </label>
  );
}
